import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Megaphone, Menu, X } from "lucide-react";
import LangToggle from "@/components/LangToggle";
import { useLang } from "@/lib/useLang";

const NAV_ITEMS = [
  { to: "/", key: "nav_home", end: true },
  { to: "/about", key: "nav_about" },
  { to: "/previous-polls", key: "nav_previous_polls" },
];

export const TopNav = () => {
  const { t } = useLang();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll while mobile drawer is open
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
    return undefined;
  }, [open]);

  return (
    <header className="border-b-4 border-black relative z-30" data-testid="top-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-4 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-3 shrink-0 min-w-0" data-testid="brand-link">
          <div
            className="border-4 border-black flex items-center justify-center shrink-0"
            style={{ background: "#0A0A0A", color: "#FF9933", width: 40, height: 40 }}
            aria-hidden="true"
          >
            <Megaphone size={20} strokeWidth={3} />
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] md:text-xs uppercase font-bold tracking-[0.3em] leading-none">
              {t("brand_super_eyebrow")}
            </p>
            <p className="font-display uppercase leading-none text-base sm:text-lg md:text-xl mt-1 truncate">
              {t("brand_super_name")}
            </p>
          </div>
        </Link>

        {/* Desktop nav (md and up) */}
        <nav className="hidden md:flex items-center gap-2" data-testid="primary-nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-link-${item.key}`}
              className={({ isActive }) =>
                `px-3 py-2 text-xs uppercase font-extrabold tracking-[0.2em] border-2 ${
                  isActive ? "border-black bg-black text-[#FF9933]" : "border-transparent hover:border-black"
                }`
              }
            >
              {t(item.key)}
            </NavLink>
          ))}
          <div className="ml-2">
            <LangToggle />
          </div>
        </nav>

        {/* Mobile burger (< md) */}
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          className="md:hidden inline-flex items-center justify-center border-4 border-black shrink-0"
          style={{ background: open ? "#0A0A0A" : "transparent", color: open ? "#FF9933" : "#0A0A0A", width: 40, height: 40 }}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} strokeWidth={3} /> : <Menu size={22} strokeWidth={3} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          className="md:hidden fixed inset-0 top-0 z-40"
          style={{ background: "rgba(0,0,0,0.45)" }}
          onClick={() => setOpen(false)}
          data-testid="mobile-menu-overlay"
        >
          <div
            className="absolute right-0 top-0 h-full w-[78%] max-w-sm border-l-4 border-black flex flex-col"
            style={{ background: "#FDFBF7" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="mobile-menu-drawer"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b-4 border-black">
              <p className="font-display uppercase text-lg leading-none">Menu</p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="inline-flex items-center justify-center border-4 border-black"
                style={{ background: "#0A0A0A", color: "#FF9933", width: 36, height: 36 }}
                data-testid="mobile-menu-close"
              >
                <X size={20} strokeWidth={3} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col p-5 gap-3" data-testid="mobile-primary-nav">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`mobile-nav-link-${item.key}`}
                  className={({ isActive }) =>
                    `block px-4 py-3 text-sm uppercase font-extrabold tracking-[0.25em] border-2 ${
                      isActive ? "border-black bg-black text-[#FF9933]" : "border-black bg-transparent hover:bg-black hover:text-[#FF9933]"
                    }`
                  }
                >
                  {t(item.key)}
                </NavLink>
              ))}
              <div className="mt-3">
                <LangToggle />
              </div>
            </nav>
            <div className="px-5 py-4 border-t-4 border-black">
              <p className="text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-500">
                ★ Kerala Speaks · 2026 ★
              </p>
            </div>
          </div>
        </div>
      )}

      <div data-testid="route-marker" data-pathname={location.pathname} className="hidden" />
    </header>
  );
};

export default TopNav;
